import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import {
  ShieldCheck, ClipboardText, Star, ShoppingCart, Plus, ArrowRight,
  PaperPlaneTilt, Sparkle, Gift, Tag, Clock, Copy, Megaphone, Fire, X
} from "@phosphor-icons/react";

const CustomerDashboard = ({ user, logout }) => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [requirements, setRequirements] = useState([]);
  const [feedbacks, setFeedbacks] = useState([]);
  const [products, setProducts] = useState([]);
  const [offers, setOffers] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [showReqForm, setShowReqForm] = useState(false);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [reqForm, setReqForm] = useState({ title: "", description: "", category: "general", budget: "", urgency: "normal" });
  const [fbForm, setFbForm] = useState({ subject: "", message: "", rating: 5 });
  const [annIndex, setAnnIndex] = useState(0);
  const [showBar, setShowBar] = useState(true);
  const [quoteModal, setQuoteModal] = useState(null); // { subject, source_id }
  const [quoteForm, setQuoteForm] = useState({ name: user?.name || "", email: user?.email || "", phone: "", company: "", message: "" });

  const fetchAll = useCallback(async () => {
    try {
      const [oRes, rRes, fRes, pRes, offRes, annRes, cRes] = await Promise.all([
        axios.get(`${API}/customer/orders?user_id=${user.user_id}`),
        axios.get(`${API}/customer/requirements?user_id=${user.user_id}`),
        axios.get(`${API}/customer/feedback?user_id=${user.user_id}`),
        axios.get(`${API}/products`),
        axios.get(`${API}/customer/offers`).catch(() => ({ data: [] })),
        axios.get(`${API}/customer/announcements`).catch(() => ({ data: [] })),
        axios.get(`${API}/customer/promo-campaigns`).catch(() => ({ data: [] })),
      ]);
      setOrders(oRes.data);
      setRequirements(rRes.data);
      setFeedbacks(fRes.data);
      setProducts(pRes.data.slice(0, 4));
      setOffers(offRes.data);
      setAnnouncements(annRes.data);
      setCampaigns(cRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [user.user_id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchAll(); }, [fetchAll]); // eslint-disable-line react-hooks/exhaustive-deps

  // Rotate top announcement bar
  useEffect(() => {
    if (announcements.length < 2) return;
    const t = setInterval(() => setAnnIndex(i => (i + 1) % announcements.length), 5000);
    return () => clearInterval(t);
  }, [announcements.length]);

  const submitRequirement = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/customer/requirements?user_id=${user.user_id}&user_name=${encodeURIComponent(user.name)}`, reqForm);
      toast.success("Requirement submitted!");
      setShowReqForm(false);
      setReqForm({ title: "", description: "", category: "general", budget: "", urgency: "normal" });
      fetchAll();
    } catch (err) { toast.error("Failed to submit"); }
  };

  const submitFeedback = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/customer/feedback?user_id=${user.user_id}&user_name=${encodeURIComponent(user.name)}`, fbForm);
      toast.success("Feedback submitted!");
      setShowFeedbackForm(false);
      setFbForm({ subject: "", message: "", rating: 5 });
      fetchAll();
    } catch (err) { toast.error("Failed to submit"); }
  };

  const placeOrder = async (product) => {
    try {
      await axios.post(`${API}/customer/orders?user_id=${user.user_id}&user_name=${encodeURIComponent(user.name)}`, {
        product_id: product.product_id, product_name: product.name, quantity: 1
      });
      toast.success(`Order placed for ${product.name}!`);
      fetchAll();
    } catch (err) { toast.error("Failed to place order"); }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success(`Code ${code} copied!`);
  };

  const redeemOffer = async (offer) => {
    try {
      await axios.post(`${API}/customer/redeem-offer?offer_id=${offer.offer_id}&user_id=${user.user_id}`);
      copyCode(offer.code);
      toast.success(`${offer.title} unlocked!`);
    } catch (err) { toast.error("Could not redeem"); }
  };

  const submitQuote = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/quote-requests`, {
        ...quoteForm,
        subject: quoteModal.subject,
        source: "promotion",
        source_id: quoteModal.source_id || ""
      });
      toast.success("Quote request sent! Our team will contact you shortly.");
      setQuoteModal(null);
      setQuoteForm({ name: user?.name || "", email: user?.email || "", phone: "", company: "", message: "" });
    } catch (err) { toast.error("Could not send quote request"); }
  };

  const handleCampaignCta = (campaign) => {
    const link = campaign.cta_link || "";
    const label = (campaign.cta_label || "").toLowerCase();
    if (label.includes("quote") || link === "#" || !link) {
      setQuoteModal({ subject: campaign.title, source_id: campaign.campaign_id });
    } else if (link.startsWith("http")) {
      window.open(link, "_blank");
    } else {
      navigate(link);
    }
  };

  const getStatusClass = (status) => {
    if (status === "completed" || status === "delivered") return "bg-green-50 text-green-700 border-green-200";
    if (status === "in_progress" || status === "processing") return "bg-blue-50 text-blue-700 border-blue-200";
    if (status === "cancelled") return "bg-red-50 text-red-700 border-red-200";
    return "bg-yellow-50 text-yellow-700 border-yellow-200";
  };

  const tabs = [
    { key: "overview", label: "Overview" },
    { key: "offers", label: `Offers (${offers.length})` },
    { key: "orders", label: `Orders (${orders.length})` },
    { key: "requirements", label: `Requirements (${requirements.length})` },
    { key: "feedback", label: `Feedback (${feedbacks.length})` },
  ];

  const currentAnn = announcements[annIndex];

  if (loading) return (
    <div className="min-h-screen bg-slate-50">
      <CustomerHeader user={user} logout={logout} navigate={navigate} />
      <div className="flex justify-center items-center py-32"><div className="loading-spinner"></div></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      {/* ANNOUNCEMENT BAR */}
      {showBar && currentAnn && (
        <div
          data-testid="announcement-bar"
          className="relative overflow-hidden text-white px-4 py-2.5 text-sm"
          style={{ background: `linear-gradient(90deg, ${currentAnn.accent || '#002FA7'}, #0f172a)` }}
        >
          <div className="max-w-6xl mx-auto flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 overflow-hidden">
              <span className="text-xl">{currentAnn.emoji || '🎉'}</span>
              <span className="font-bold uppercase tracking-[0.15em] text-xs hidden sm:inline">{currentAnn.title}</span>
              <span className="truncate">{currentAnn.message}</span>
              {currentAnn.link && currentAnn.link !== '#' && (
                <button
                  onClick={() => navigate(currentAnn.link)}
                  className="hidden md:inline-flex items-center gap-1 font-bold underline underline-offset-2 hover:opacity-90 whitespace-nowrap"
                  data-testid="announcement-cta"
                >
                  Shop Now <ArrowRight size={14} weight="bold" />
                </button>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {announcements.length > 1 && (
                <div className="flex gap-1">
                  {announcements.map((a, i) => (
                    <button key={a.announcement_id} onClick={() => setAnnIndex(i)}
                      className={`h-1.5 rounded-full transition-all duration-300 ${i === annIndex ? 'w-6 bg-white' : 'w-1.5 bg-white/40'}`} />
                  ))}
                </div>
              )}
              <button onClick={() => setShowBar(false)} className="hover:bg-white/10 p-1" data-testid="close-announcement">
                <X size={14} weight="bold" />
              </button>
            </div>
          </div>
        </div>
      )}

      <CustomerHeader user={user} logout={logout} navigate={navigate} />

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Welcome */}
        <div className="mb-8 flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-4xl font-bold mb-1" style={{fontFamily: 'Outfit, sans-serif'}}>Welcome, {user.name.split(' ')[0]} 👋</h1>
            <p className="text-slate-600">Your security hub — deals, orders, support all in one place.</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 bg-white border border-slate-200 px-4 py-2">
              <Sparkle size={16} weight="fill" className="text-[#F59E0B]" />
              <span className="text-xs uppercase tracking-wider font-bold text-slate-600">Loyalty</span>
              <span className="font-bold text-[#002FA7]">Silver Tier</span>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-8 border-b border-slate-200 pb-0 overflow-x-auto">
          {tabs.map(t => (
            <button key={t.key} data-testid={`tab-${t.key}`} onClick={() => setActiveTab(t.key)}
              className={`px-5 py-3 text-sm font-medium transition-colors duration-300 border-b-2 -mb-px whitespace-nowrap ${activeTab === t.key ? 'border-[#002FA7] text-[#002FA7]' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* OVERVIEW */}
        {activeTab === "overview" && (
          <>
            {/* HERO OFFER */}
            {offers[0] && <HeroOffer offer={offers[0]} onGrab={redeemOffer} />}

            {/* STATS */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 my-8">
              <StatCard icon={ShoppingCart} color="#002FA7" value={orders.length} label="My Orders" />
              <StatCard icon={ClipboardText} color="#10B981" value={requirements.length} label="Requirements" />
              <StatCard icon={Star} color="#F59E0B" value={feedbacks.length} label="Feedback" />
              <StatCard icon={Gift} color="#DC2626" value={offers.length} label="Active Offers" />
            </div>

            {/* OFFERS GRID */}
            {offers.length > 1 && (
              <section className="mb-10">
                <div className="flex justify-between items-end mb-4">
                  <div>
                    <h3 className="text-2xl font-bold" style={{fontFamily: 'Outfit, sans-serif'}}>Festive Deals</h3>
                    <p className="text-sm text-slate-500">Handpicked offers just for you</p>
                  </div>
                  <button onClick={() => setActiveTab("offers")} className="text-sm text-[#002FA7] font-bold flex items-center gap-1 hover:underline">
                    View all <ArrowRight size={14} />
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {offers.slice(1, 4).map(o => <OfferCard key={o.offer_id} offer={o} onGrab={redeemOffer} />)}
                </div>
              </section>
            )}

            {/* CAMPAIGNS */}
            {campaigns.length > 0 && (
              <section className="mb-10">
                <div className="mb-4">
                  <h3 className="text-2xl font-bold" style={{fontFamily: 'Outfit, sans-serif'}}>Featured Campaigns</h3>
                  <p className="text-sm text-slate-500">Curated bundles and service plans</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  {campaigns.map(c => <CampaignCard key={c.campaign_id} campaign={c} onCta={handleCampaignCta} />)}
                </div>
              </section>
            )}

            {/* QUICK ACTIONS */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
              <QuickAction icon={ClipboardText} title="Submit a Requirement" subtitle="Tell us what security solution you need"
                color="#002FA7" dataTestId="quick-submit-req"
                onClick={() => { setActiveTab("requirements"); setShowReqForm(true); }} />
              <QuickAction icon={Star} title="Give Feedback" subtitle="Share your experience with our team"
                color="#F59E0B" dataTestId="quick-give-feedback"
                onClick={() => { setActiveTab("feedback"); setShowFeedbackForm(true); }} />
            </div>

            {/* FEATURED PRODUCTS */}
            <section>
              <div className="flex justify-between items-end mb-4">
                <div>
                  <h3 className="text-2xl font-bold" style={{fontFamily: 'Outfit, sans-serif'}}>Featured Products</h3>
                  <p className="text-sm text-slate-500">Top picks from trusted brands</p>
                </div>
                <button onClick={() => navigate('/products')} className="text-sm text-[#002FA7] font-bold flex items-center gap-1 hover:underline">
                  Browse catalog <ArrowRight size={14} />
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {products.map(p => (
                  <div key={p.product_id} className="bg-white border border-slate-200 rounded-none overflow-hidden hover:shadow-md transition-all duration-300 group">
                    {p.image_url && <div className="overflow-hidden"><img src={p.image_url} alt={p.name} className="w-full h-36 object-cover group-hover:scale-105 transition-transform duration-500" /></div>}
                    <div className="p-4">
                      <h4 className="font-semibold text-sm mb-1 line-clamp-1">{p.name}</h4>
                      <p className="text-lg font-bold text-[#002FA7]" style={{fontFamily: 'Outfit, sans-serif'}}>₹{p.price?.toLocaleString('en-IN')}</p>
                      <div className="flex gap-2 mt-3">
                        <button data-testid={`order-${p.product_id}`} onClick={() => placeOrder(p)}
                          className="flex-1 rounded-none bg-[#002FA7] text-white text-xs py-2 font-medium hover:bg-[#00227A] transition-colors duration-300">Order</button>
                        <button onClick={() => navigate(`/products/${p.product_id}`)}
                          className="rounded-none border border-slate-300 text-xs px-3 py-2 hover:bg-slate-50 transition-colors duration-300">View</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </>
        )}

        {/* OFFERS TAB */}
        {activeTab === "offers" && (
          <>
            <div className="mb-6">
              <h3 className="text-2xl font-bold flex items-center gap-2" style={{fontFamily: 'Outfit, sans-serif'}}>
                <Fire size={26} weight="fill" className="text-[#DC2626]" /> All Active Offers
              </h3>
              <p className="text-sm text-slate-500">Grab them before they're gone</p>
            </div>
            {offers.length === 0 ? (
              <p className="text-slate-500 text-center py-12">No active offers at the moment.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {offers.map(o => <OfferCard key={o.offer_id} offer={o} onGrab={redeemOffer} />)}
              </div>
            )}
          </>
        )}

        {/* ORDERS */}
        {activeTab === "orders" && (
          <div className="space-y-4">
            {orders.length === 0 ? <p className="text-slate-500 text-center py-12">No orders yet. Browse products to place your first order.</p> : orders.map(o => (
              <div key={o.order_id} className="bg-white border border-slate-200 rounded-none p-5 shadow-sm flex items-center justify-between hover:shadow-md transition-shadow duration-300">
                <div>
                  <h4 className="font-semibold">{o.product_name}</h4>
                  <p className="text-sm text-slate-600">Qty: {o.quantity} | ₹{o.total?.toLocaleString('en-IN')}</p>
                  <p className="text-xs text-slate-400 mt-1">{new Date(o.created_at).toLocaleDateString()}</p>
                </div>
                <span className={`rounded-none px-3 py-1 text-xs font-bold uppercase tracking-wider border ${getStatusClass(o.status)}`}>{o.status}</span>
              </div>
            ))}
          </div>
        )}

        {/* REQUIREMENTS */}
        {activeTab === "requirements" && (
          <>
            <div className="flex justify-end mb-6">
              <button data-testid="new-req-btn" onClick={() => setShowReqForm(!showReqForm)}
                className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-6 py-3 font-medium flex items-center gap-2 transition-colors duration-300">
                <Plus size={18} /> {showReqForm ? 'Cancel' : 'New Requirement'}
              </button>
            </div>
            {showReqForm && (
              <div data-testid="req-form" className="bg-white border border-slate-200 rounded-none p-8 mb-6 shadow-sm">
                <h3 className="text-xl font-semibold mb-4" style={{fontFamily: 'Outfit, sans-serif'}}>Submit Requirement</h3>
                <form onSubmit={submitRequirement} className="grid grid-cols-2 gap-6">
                  <div className="col-span-2">
                    <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Title</label>
                    <input data-testid="req-title" type="text" value={reqForm.title} onChange={e => setReqForm({...reqForm, title: e.target.value})} className="rounded-none bg-white border border-slate-300 focus:border-[#002FA7] p-3 w-full outline-none" required />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Category</label>
                    <select value={reqForm.category} onChange={e => setReqForm({...reqForm, category: e.target.value})} className="rounded-none bg-white border border-slate-300 p-3 w-full outline-none">
                      <option value="general">General</option>
                      <option value="camera">Camera</option>
                      <option value="alarm">Alarm System</option>
                      <option value="access_control">Access Control</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Urgency</label>
                    <select value={reqForm.urgency} onChange={e => setReqForm({...reqForm, urgency: e.target.value})} className="rounded-none bg-white border border-slate-300 p-3 w-full outline-none">
                      <option value="low">Low</option>
                      <option value="normal">Normal</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Budget</label>
                    <input type="text" value={reqForm.budget} onChange={e => setReqForm({...reqForm, budget: e.target.value})} placeholder="e.g. ₹10,000 - ₹50,000" className="rounded-none bg-white border border-slate-300 p-3 w-full outline-none" />
                  </div>
                  <div className="col-span-2">
                    <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Description</label>
                    <textarea data-testid="req-desc" value={reqForm.description} onChange={e => setReqForm({...reqForm, description: e.target.value})} rows="4" className="rounded-none bg-white border border-slate-300 focus:border-[#002FA7] p-3 w-full outline-none" required />
                  </div>
                  <div className="col-span-2">
                    <button type="submit" data-testid="submit-req-btn" className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-8 py-3 font-medium flex items-center gap-2">
                      <PaperPlaneTilt size={18} /> Submit Requirement
                    </button>
                  </div>
                </form>
              </div>
            )}
            <div className="space-y-4">
              {requirements.length === 0 ? <p className="text-slate-500 text-center py-12">No requirements submitted yet.</p> : requirements.map(r => (
                <div key={r.requirement_id} className="bg-white border border-slate-200 rounded-none p-5 shadow-sm">
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-semibold">{r.title}</h4>
                      <p className="text-sm text-slate-600 mt-1">{r.description}</p>
                      <div className="flex gap-3 mt-2 text-xs text-slate-500">
                        <span>Category: {r.category}</span>
                        {r.budget && <span>Budget: {r.budget}</span>}
                        <span>Urgency: {r.urgency}</span>
                      </div>
                    </div>
                    <span className={`rounded-none px-3 py-1 text-xs font-bold uppercase tracking-wider border ${getStatusClass(r.status)}`}>{r.status}</span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* FEEDBACK */}
        {activeTab === "feedback" && (
          <>
            <div className="flex justify-end mb-6">
              <button data-testid="new-fb-btn" onClick={() => setShowFeedbackForm(!showFeedbackForm)}
                className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-6 py-3 font-medium flex items-center gap-2 transition-colors duration-300">
                <Plus size={18} /> {showFeedbackForm ? 'Cancel' : 'Give Feedback'}
              </button>
            </div>
            {showFeedbackForm && (
              <div data-testid="fb-form" className="bg-white border border-slate-200 rounded-none p-8 mb-6 shadow-sm">
                <h3 className="text-xl font-semibold mb-4" style={{fontFamily: 'Outfit, sans-serif'}}>Submit Feedback</h3>
                <form onSubmit={submitFeedback} className="space-y-6">
                  <div>
                    <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Subject</label>
                    <input data-testid="fb-subject" type="text" value={fbForm.subject} onChange={e => setFbForm({...fbForm, subject: e.target.value})} className="rounded-none bg-white border border-slate-300 focus:border-[#002FA7] p-3 w-full outline-none" required />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Rating</label>
                    <div className="flex gap-2">
                      {[1,2,3,4,5].map(n => (
                        <button key={n} type="button" data-testid={`rating-${n}`} onClick={() => setFbForm({...fbForm, rating: n})}
                          className={`w-10 h-10 flex items-center justify-center rounded-none border font-bold transition-colors duration-300 ${fbForm.rating >= n ? 'bg-[#F59E0B] text-white border-[#F59E0B]' : 'bg-white text-slate-400 border-slate-300'}`}>
                          {n}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Message</label>
                    <textarea data-testid="fb-message" value={fbForm.message} onChange={e => setFbForm({...fbForm, message: e.target.value})} rows="4" className="rounded-none bg-white border border-slate-300 focus:border-[#002FA7] p-3 w-full outline-none" required />
                  </div>
                  <button type="submit" data-testid="submit-fb-btn" className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-8 py-3 font-medium">Submit Feedback</button>
                </form>
              </div>
            )}
            <div className="space-y-4">
              {feedbacks.length === 0 ? <p className="text-slate-500 text-center py-12">No feedback submitted yet.</p> : feedbacks.map(f => (
                <div key={f.feedback_id} className="bg-white border border-slate-200 rounded-none p-5 shadow-sm">
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-semibold">{f.subject}</h4>
                      <p className="text-sm text-slate-600 mt-1">{f.message}</p>
                      <p className="text-xs text-slate-400 mt-2">{new Date(f.created_at).toLocaleDateString()}</p>
                    </div>
                    <div className="flex gap-0.5">
                      {[1,2,3,4,5].map(n => (
                        <Star key={n} size={16} weight={f.rating >= n ? "fill" : "regular"} className={f.rating >= n ? "text-[#F59E0B]" : "text-slate-300"} />
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* QUOTE REQUEST MODAL */}
      {quoteModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setQuoteModal(null)}>
          <div data-testid="quote-modal" className="bg-white border border-slate-200 rounded-none shadow-xl max-w-lg w-full" onClick={e => e.stopPropagation()}>
            <div className="border-b border-slate-200 px-6 py-4 flex justify-between items-center">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold">Request Quote</div>
                <h3 className="text-xl font-bold" style={{fontFamily: 'Outfit, sans-serif'}}>{quoteModal.subject}</h3>
              </div>
              <button onClick={() => setQuoteModal(null)} data-testid="quote-close" className="p-2 hover:bg-slate-100"><X size={18} /></button>
            </div>
            <form onSubmit={submitQuote} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <input data-testid="quote-name" required placeholder="Name" value={quoteForm.name} onChange={e => setQuoteForm({...quoteForm, name: e.target.value})}
                  className="rounded-none border border-slate-300 focus:border-[#002FA7] p-3 outline-none" />
                <input data-testid="quote-email" type="email" required placeholder="Email" value={quoteForm.email} onChange={e => setQuoteForm({...quoteForm, email: e.target.value})}
                  className="rounded-none border border-slate-300 focus:border-[#002FA7] p-3 outline-none" />
                <input data-testid="quote-phone" placeholder="Phone" value={quoteForm.phone} onChange={e => setQuoteForm({...quoteForm, phone: e.target.value})}
                  className="rounded-none border border-slate-300 focus:border-[#002FA7] p-3 outline-none" />
                <input data-testid="quote-company" placeholder="Company (optional)" value={quoteForm.company} onChange={e => setQuoteForm({...quoteForm, company: e.target.value})}
                  className="rounded-none border border-slate-300 focus:border-[#002FA7] p-3 outline-none" />
              </div>
              <textarea data-testid="quote-message" required rows={4} placeholder="Tell us about your requirement, property size, number of cameras/doors, etc." value={quoteForm.message} onChange={e => setQuoteForm({...quoteForm, message: e.target.value})}
                className="w-full rounded-none border border-slate-300 focus:border-[#002FA7] p-3 outline-none text-sm" />
              <button data-testid="quote-submit" type="submit" className="w-full rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-8 py-3 font-bold uppercase tracking-wider transition-colors duration-300">
                Send Quote Request
              </button>
              <p className="text-xs text-slate-500 text-center">Our team typically responds within 4 business hours.</p>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

/* ---------- Subcomponents ---------- */

const CustomerHeader = ({ user, logout, navigate }) => (
  <header className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-40">
    <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
      <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
        <ShieldCheck size={28} weight="duotone" className="text-[#002FA7]" />
        <span className="text-lg font-bold" style={{fontFamily: 'Outfit, sans-serif'}}>SwifTech India</span>
      </div>
      <nav className="hidden md:flex items-center gap-1">
        <button onClick={() => navigate('/products')} className="text-sm text-slate-600 hover:text-[#002FA7] px-3 py-2 transition-colors duration-300">Products</button>
        <button onClick={() => navigate('/dashboard')} className="text-sm text-slate-600 hover:text-[#002FA7] px-3 py-2 transition-colors duration-300">My Dashboard</button>
      </nav>
      <div className="flex items-center gap-3">
        <span className="text-sm text-slate-500 hidden sm:inline">{user.name}</span>
        <button data-testid="customer-logout-btn" onClick={logout}
          className="rounded-none border border-slate-300 text-slate-700 hover:bg-slate-50 px-4 py-2 text-sm font-medium transition-colors duration-300">
          Logout
        </button>
      </div>
    </div>
  </header>
);

const StatCard = ({ icon: Icon, color, value, label }) => (
  <div className="bg-white border border-slate-200 rounded-none p-5 shadow-sm hover:shadow-md transition-shadow duration-300">
    <Icon size={24} weight="duotone" style={{color}} className="mb-2" />
    <div className="text-3xl font-bold" style={{fontFamily: 'Outfit, sans-serif', color}}>{value}</div>
    <div className="text-xs text-slate-500 mt-1 uppercase tracking-wider">{label}</div>
  </div>
);

const HeroOffer = ({ offer, onGrab }) => {
  const countdown = useCountdown(offer.valid_until);
  return (
    <div
      data-testid="hero-offer"
      className="relative overflow-hidden rounded-none shadow-lg border border-slate-200"
      style={{ background: `linear-gradient(120deg, ${offer.bg_color || '#002FA7'}, #0f172a)` }}
    >
      {offer.image_url && (
        <img src={offer.image_url} alt={offer.title}
          className="absolute inset-0 w-full h-full object-cover opacity-25 mix-blend-overlay" />
      )}
      <div className="relative grid grid-cols-1 md:grid-cols-2 gap-6 p-8 md:p-12 text-white">
        <div>
          <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm border border-white/20 px-3 py-1 mb-4">
            <Sparkle size={14} weight="fill" /> <span className="text-xs uppercase tracking-[0.2em] font-bold">Flash Offer</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-3 leading-tight" style={{fontFamily: 'Outfit, sans-serif'}}>
            {offer.title}
          </h2>
          <p className="text-white/85 text-base mb-6 max-w-md">{offer.description}</p>
          <div className="flex flex-wrap items-center gap-3">
            <div className="bg-white/10 border border-white/25 px-4 py-3 backdrop-blur-sm">
              <div className="text-xs uppercase tracking-wider opacity-75">Use Code</div>
              <div className="text-xl font-black tracking-wider" style={{fontFamily: 'monospace'}}>{offer.code}</div>
            </div>
            <button
              data-testid="hero-grab-btn"
              onClick={() => onGrab(offer)}
              className="rounded-none bg-white text-slate-900 font-bold px-6 py-4 text-sm uppercase tracking-wider hover:bg-yellow-200 transition-colors duration-300 flex items-center gap-2"
            >
              Grab Offer <ArrowRight size={16} weight="bold" />
            </button>
          </div>
        </div>
        <div className="flex flex-col justify-between items-start md:items-end gap-4">
          <div className="text-right">
            <div className="text-[4rem] md:text-[5.5rem] font-black leading-none" style={{fontFamily: 'Outfit, sans-serif'}}>
              {offer.discount}
            </div>
          </div>
          {countdown && (
            <div className="flex gap-2 items-center">
              <Clock size={18} weight="fill" />
              <span className="text-xs uppercase tracking-wider opacity-75 mr-1">Ends in</span>
              <CountdownUnit value={countdown.days} label="d" />
              <CountdownUnit value={countdown.hours} label="h" />
              <CountdownUnit value={countdown.minutes} label="m" />
              <CountdownUnit value={countdown.seconds} label="s" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const CountdownUnit = ({ value, label }) => (
  <div className="bg-black/30 border border-white/15 px-3 py-1.5 min-w-[3rem] text-center">
    <div className="text-lg font-bold leading-tight" style={{fontFamily: 'monospace'}}>{String(value).padStart(2, '0')}</div>
    <div className="text-[0.55rem] uppercase opacity-70">{label}</div>
  </div>
);

const OfferCard = ({ offer, onGrab }) => (
  <div data-testid={`offer-${offer.offer_id}`} className="bg-white border border-slate-200 rounded-none overflow-hidden hover:shadow-lg transition-shadow duration-300 group flex flex-col">
    <div
      className="relative h-32 flex items-end p-4 text-white overflow-hidden"
      style={{ background: `linear-gradient(135deg, ${offer.bg_color || '#002FA7'}, #0f172a)` }}
    >
      {offer.image_url && (
        <img src={offer.image_url} alt={offer.title} className="absolute inset-0 w-full h-full object-cover opacity-30 group-hover:scale-110 transition-transform duration-500" />
      )}
      <div className="relative z-10">
        <div className="inline-flex items-center gap-1 bg-white/20 backdrop-blur-sm px-2 py-0.5 text-[0.65rem] uppercase tracking-wider font-bold mb-1">
          <Tag size={10} weight="fill" /> {offer.category}
        </div>
        <div className="text-2xl font-black" style={{fontFamily: 'Outfit, sans-serif'}}>{offer.discount}</div>
      </div>
    </div>
    <div className="p-5 flex-1 flex flex-col">
      <h4 className="font-bold text-lg mb-1" style={{fontFamily: 'Outfit, sans-serif'}}>{offer.title}</h4>
      <p className="text-sm text-slate-600 mb-4 flex-1">{offer.description}</p>
      <div className="flex items-center justify-between gap-3 mt-auto">
        <button
          onClick={() => onGrab(offer)}
          data-testid={`grab-${offer.offer_id}`}
          className="group/c flex-1 flex items-center justify-between bg-slate-50 border border-dashed border-slate-300 hover:border-[#002FA7] hover:bg-[#002FA7]/5 transition-colors duration-300 px-3 py-2"
        >
          <span className="font-mono font-bold text-sm text-slate-800">{offer.code}</span>
          <Copy size={14} className="text-slate-400 group-hover/c:text-[#002FA7]" />
        </button>
      </div>
    </div>
  </div>
);

const CampaignCard = ({ campaign, onCta }) => (
  <div data-testid={`campaign-${campaign.campaign_id}`} className="bg-white border border-slate-200 rounded-none overflow-hidden group hover:shadow-lg transition-shadow duration-300 flex flex-col">
    <div className="relative h-44 overflow-hidden">
      {campaign.image_url && (
        <img src={campaign.image_url} alt={campaign.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
      <div className="absolute top-3 left-3">
        <span className="inline-flex items-center gap-1 text-white text-[0.65rem] uppercase tracking-[0.2em] font-bold px-2 py-1" style={{ backgroundColor: campaign.accent || '#002FA7' }}>
          <Megaphone size={10} weight="fill" /> {campaign.badge}
        </span>
      </div>
      <div className="absolute bottom-3 left-3 right-3 text-white">
        <div className="text-xs uppercase tracking-[0.15em] opacity-85 mb-1">{campaign.tagline}</div>
        <h4 className="text-xl font-bold leading-tight" style={{fontFamily: 'Outfit, sans-serif'}}>{campaign.title}</h4>
      </div>
    </div>
    <div className="p-5 flex-1 flex flex-col">
      <p className="text-sm text-slate-600 mb-4 flex-1">{campaign.description}</p>
      <button
        onClick={() => onCta(campaign)}
        data-testid={`campaign-cta-${campaign.campaign_id}`}
        className="w-full rounded-none bg-slate-900 text-white hover:bg-[#002FA7] transition-colors duration-300 font-bold text-sm uppercase tracking-wider py-3 flex items-center justify-center gap-2"
      >
        {campaign.cta_label} <ArrowRight size={14} weight="bold" />
      </button>
    </div>
  </div>
);

const QuickAction = ({ icon: Icon, title, subtitle, color, onClick, dataTestId }) => (
  <button data-testid={dataTestId} onClick={onClick}
    className="bg-white border border-slate-200 rounded-none p-6 shadow-sm text-left hover:shadow-md hover:border-slate-300 transition-all duration-300 flex items-center gap-4 group">
    <div className="p-3 transition-colors duration-300" style={{ backgroundColor: `${color}15` }}>
      <Icon size={24} weight="fill" style={{ color }} />
    </div>
    <div className="flex-1">
      <h3 className="font-semibold" style={{fontFamily: 'Outfit, sans-serif'}}>{title}</h3>
      <p className="text-sm text-slate-500">{subtitle}</p>
    </div>
    <ArrowRight size={20} className="text-slate-400 group-hover:text-[#002FA7] group-hover:translate-x-1 transition-all duration-300" />
  </button>
);

/* ---------- Hooks ---------- */

function useCountdown(isoDate) {
  const target = useMemo(() => isoDate ? new Date(isoDate).getTime() : null, [isoDate]);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!target) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [target]);
  if (!target) return null;
  const diff = Math.max(0, target - now);
  return {
    days: Math.floor(diff / 86400000),
    hours: Math.floor((diff % 86400000) / 3600000),
    minutes: Math.floor((diff % 3600000) / 60000),
    seconds: Math.floor((diff % 60000) / 1000),
  };
}

export default CustomerDashboard;
